import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../lib/supabaseClient";
import { useAuth } from "../../context/AuthContext.jsx";
import { MORNING_PRACTICES } from "./data.js";
import Celebration from "../../components/Celebration.jsx";

const LEVEL_ID = "inner-citadel";

function computeOutcome(dichotomyResult, systemPlan, pauseResult) {
  const { log } = pauseResult;

  const dichotomyScore = dichotomyResult.correctCount * 15;
  const systemBonus = 50;

  const goodCount = log.filter((l) => l.choice.tag === "good").length;
  const weakCount = log.filter((l) => l.choice.tag === "weak").length;
  const badCount = log.filter((l) => l.choice.tag === "bad").length;
  const pauseScore = goodCount * 20 - weakCount * 10 - badCount * 20;

  const score = Math.max(50, dichotomyScore + systemBonus + pauseScore + 50);

  let verdict;
  if (goodCount >= 5) {
    verdict = { label: "Citadel Strong", tone: "lime" };
  } else if (goodCount >= 3) {
    verdict = { label: "Citadel Standing", tone: "gold" };
  } else {
    verdict = { label: "Walls Still Going Up \u2014 That's Normal", tone: "danger" };
  }

  return { dichotomyScore, systemBonus, pauseScore, goodCount, weakCount, badCount, verdict, score };
}

export default function DebriefStage({ dichotomyResult, systemPlan, pauseResult, onRetry }) {
  const navigate = useNavigate();
  const { user, profile, refreshProfile } = useAuth();
  const [saving, setSaving] = useState(true);
  const [saved, setSaved] = useState(false);

  const outcome = computeOutcome(dichotomyResult, systemPlan, pauseResult);
  const didLevelUp = Math.floor(((profile?.xp ?? 0) + outcome.score) / 500) + 1 > (profile?.level ?? 1);
  const chosenPractices = MORNING_PRACTICES.filter((p) => systemPlan.practices.includes(p.id));

  useEffect(() => {
    let cancelled = false;

    async function save() {
      if (!user) {
        setSaving(false);
        return;
      }
      const newXp = (profile?.xp ?? 0) + outcome.score;
      const newLevel = Math.max(profile?.level ?? 1, Math.floor(newXp / 500) + 1);
      const newNetWorth = (profile?.net_worth_cents ?? 0) + outcome.score * 100;

      const { error } = await supabase
        .from("profiles")
        .update({ xp: newXp, level: newLevel, net_worth_cents: newNetWorth })
        .eq("id", user.id);

      if (!cancelled) {
        if (!error) {
          await refreshProfile();
          const { error: challengeError } = await supabase.rpc("record_level_result", {
            p_level_id: LEVEL_ID,
            p_score: outcome.score,
          });
          if (challengeError) {
            console.error("Failed to record challenge result:", challengeError.message);
          }
          setSaved(true);
        } else {
          console.error("Failed to save The Inner Citadel results:", error.message);
        }
        setSaving(false);
      }
    }

    save();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toneClasses = {
    lime: "border-lime bg-lime/10 text-lime",
    gold: "border-gold bg-gold/10 text-gold",
    danger: "border-danger bg-danger/10 text-danger",
  };

  return (
    <div className="animate-popIn text-center">
      <Celebration show={saved} levelUp={didLevelUp} />
      <h2 className="pixel-heading mb-1 text-lg text-lime">CITADEL REPORT</h2>
      <p className="mb-6 text-xs uppercase tracking-widest text-mist/50">Today's Run</p>

      <div className={`mx-auto mb-4 max-w-sm border-2 p-5 ${toneClasses[outcome.verdict.tone]}`}>
        <p className="label-pixel mb-1">Verdict</p>
        <p className="font-display text-base">{outcome.verdict.label}</p>
      </div>

      <div className="mx-auto mb-6 max-w-sm space-y-2 border-2 border-black bg-ink p-4 text-left text-sm">
        <div className="flex justify-between">
          <span className="text-mist/60">Dichotomy Sorted Correctly</span>
          <span className="text-cyan">{dichotomyResult.correctCount}/8</span>
        </div>
        <div className="flex justify-between text-[11px] text-mist/50">
          <span>Your System</span>
          <span className="text-right text-mist">{chosenPractices.map((p) => p.label).join(" · ")}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-mist/60">Wise Pauses</span>
          <span className="text-lime">{outcome.goodCount}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-mist/60">Paused, but Still Stuck</span>
          <span className="text-gold">{outcome.weakCount}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-mist/60">Reacted Without Pausing</span>
          <span className="text-danger">{outcome.badCount}</span>
        </div>
        <div className="relative flex justify-between border-t border-black/50 pt-2 font-display text-[11px]">
          <span className="text-mist/60">SCORE</span>
          <span className="text-gold">{outcome.score.toLocaleString()}</span>
          {saved && (
            <span className="absolute -top-4 right-0 animate-floatUp text-[9px] text-lime">+{outcome.score} XP</span>
          )}
        </div>
      </div>

      <p className="mx-auto mb-6 max-w-sm text-[11px] text-mist/50">
        One more thing, briefly — not to be heavy, just useful: you don't get today back. That's a
        pretty good reason not to burn the whole evening replaying one bad comment or one bad
        grade. Handle it, then let the day keep going.
      </p>

      <p className="mb-6 text-xs text-mist/50">
        {saving ? "Saving to your profile…" : saved ? "Net worth and XP updated on your profile ✅" : "Results ready."}
      </p>

      <div className="flex flex-wrap justify-center gap-3">
        <button onClick={onRetry} className="btn-magenta">
          ↻ RUN IT AGAIN
        </button>
        <button onClick={() => navigate("/")} className="btn-cyan">
          LEVEL SELECT
        </button>
      </div>
    </div>
  );
}
